import { api } from '@/shared/lib/api'
import type { Point, PointFilters } from '../types'

interface PointPaginatedResponse {
  data: Point[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

interface PointCreatePayload {
  id_client: number
  id_user: number
  id_branch_office: number
  id_company: number
  amount: number
  transaction_type: 'add' | 'spend'
  points: number
  is_valid: boolean
  comment: string
}

function buildFilterParams(filters: PointFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: filters.page,
    limit: filters.limit,
  }
  if (filters.transaction_type) params.transaction_type = filters.transaction_type
  if (filters.is_valid !== undefined) params.is_valid = filters.is_valid
  if (filters.date_from) params.date_from = filters.date_from
  if (filters.date_to) params.date_to = filters.date_to
  if (filters.amount_min !== undefined) params.amount_min = filters.amount_min
  if (filters.amount_max !== undefined) params.amount_max = filters.amount_max
  if (filters.points_min !== undefined) params.points_min = filters.points_min
  if (filters.points_max !== undefined) params.points_max = filters.points_max
  if (filters.search) params.search = filters.search
  if (filters.sort_by) params.sort_by = filters.sort_by
  if (filters.sort_order) params.sort_order = filters.sort_order
  return params
}

export const pointService = {
  getById: async (id: number): Promise<Point> => {
    const { data } = await api.get(`/point/${id}`)
    return data
  },

  getAll: async (filters: PointFilters): Promise<PointPaginatedResponse> => {
    const { data } = await api.get('/points/', {
      params: buildFilterParams(filters),
    })
    return data
  },

  getByClientId: async (clientId: number): Promise<Point[]> => {
    const { data } = await api.get(`/points/${clientId}`)
    return data
  },

  create: async (point: PointCreatePayload): Promise<Point> => {
    const { data } = await api.post('/points/', point)
    return data
  },

  invalidate: async (pointId: number): Promise<Point> => {
    const { data } = await api.put('/invalidate_point/', null, {
      params: { point_id: pointId },
    })
    return data
  },

  exportAll: async (filters: Omit<PointFilters, 'page' | 'limit'>): Promise<Blob> => {
    const params: Record<string, string | number | boolean> = {}
    if (filters.transaction_type) params.transaction_type = filters.transaction_type
    if (filters.is_valid !== undefined) params.is_valid = filters.is_valid
    if (filters.date_from) params.date_from = filters.date_from
    if (filters.date_to) params.date_to = filters.date_to
    if (filters.amount_min !== undefined) params.amount_min = filters.amount_min
    if (filters.amount_max !== undefined) params.amount_max = filters.amount_max
    if (filters.points_min !== undefined) params.points_min = filters.points_min
    if (filters.points_max !== undefined) params.points_max = filters.points_max
    if (filters.search) params.search = filters.search
    if (filters.sort_by) params.sort_by = filters.sort_by
    if (filters.sort_order) params.sort_order = filters.sort_order

    const response = await api.get('/export_points/', {
      params,
      responseType: 'blob',
    })
    return response.data
  },
}
